// The Cyber Guy — Video Library
// Real videos from https://youtube.com/@thecyberguykvc
// To add a new video: append an object with youtubeId (11 chars) and the rest is derived.

export const CYBER_GUY_CHANNEL_URL = 'https://youtube.com/@thecyberguykvc';

export const watchUrl = (id) => `https://youtube.com/shorts/${id}`;
export const embedUrl = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
export const thumbUrl = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// Category filter list (used as UI filters on The Cyber Guy page)
export const CATEGORIES = [
  'All Videos',
  'Cybersecurity Basics',
  'Identity & Access',
  'Data Protection',
  'Vulnerability Management',
  'Endpoint & Network Security',
  'Security Awareness',
  'Business Continuity',
  'Real Cyber Attacks',
  'Business Cybersecurity',
];

// Six Kennyvicops services (single source of truth mirrored from data/services.js)
export const SERVICES_MAP = {
  'Security Assessment': 'security-assessment',
  'Vulnerability Management': 'vulnerability-management',
  'Identity & Access Security': 'identity-access-security',
  'Endpoint & Network Security': 'endpoint-network-security',
  'Data Protection & Backup': 'data-protection-backup',
  'Security Awareness & Training': 'security-awareness-training',
};

// slug helper (used for /the-cyber-guy/:slug — YouTube ID is stable + unique)
export const videoSlug = (v) => v.youtubeId;

export const VIDEOS = [
  {
    id: 'network-access',
    title: '🚨 WHO IS ALLOWED INTO YOUR NETWORK?',
    description:
      'A simple look at who actually has access to your network — and why controlling that list is a foundational security control.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'PTb3LNjnH4w',
    featured: true,
    order: 1,
  },
  {
    id: 'access-what',
    title: '👤 WHO HAS ACCESS TO WHAT?',
    description:
      'Least privilege made simple — the right people, the right access, and nothing more.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'neRlFxXvbRw',
    featured: true,
    order: 2,
  },
  {
    id: 'mfa-already',
    title: '💳 YOU ALREADY USE MFA — YOU JUST DON\'T KNOW IT',
    description:
      'Multi-factor authentication explained through everyday examples you already trust.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'fIVOFIfWVqs',
    featured: false,
    order: 3,
  },
  {
    id: 'too-much-access',
    title: '⚠️ TOO MUCH ACCESS = TOO MUCH RISK',
    description:
      'Why over-permissioned accounts quietly become one of the biggest risks in a business.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'M711uWQy1Is',
    featured: false,
    order: 4,
  },
  {
    id: 'hotel-key',
    title: '🏨 WHAT DOES A HOTEL ROOM KEY HAVE TO DO WITH CYBERSECURITY?',
    description:
      'Access controls made simple — using a hotel key card to explain how modern identity works.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'UXNmQcLaV78',
    featured: false,
    order: 5,
  },
  {
    id: 'one-password',
    title: '🚨 ONE PASSWORD. MULTIPLE ACCOUNTS. ONE BIG MISTAKE.',
    description:
      'Password reuse is one of the fastest paths from a small leak to a full account takeover.',
    category: 'Identity & Access',
    service: 'Identity & Access Security',
    youtubeId: 'wNuK24rFXq4',
    featured: false,
    order: 6,
  },

  // Data Protection
  {
    id: 'locked-box',
    title: '📦 WHAT DOES A LOCKED BOX HAVE TO DO WITH ENCRYPTION?',
    description:
      'Encryption in plain English — what it protects, what it doesn\'t, and why every business should understand the difference.',
    category: 'Data Protection',
    service: 'Data Protection & Backup',
    youtubeId: 'JP67zXVsuF0',
    featured: true,
    order: 7,
  },
  {
    id: 'stop-data-leaking',
    title: '🚨 CAN YOUR BUSINESS STOP DATA FROM LEAKING?',
    description:
      'Data Loss Prevention explained — how businesses spot and stop sensitive data leaving where it shouldn\'t.',
    category: 'Data Protection',
    service: 'Data Protection & Backup',
    youtubeId: '8gsb2cl1mj4',
    featured: false,
    order: 8,
  },
  {
    id: 'protecting-all-data',
    title: '🚨 ARE YOU PROTECTING ALL YOUR DATA THE SAME WAY?',
    description:
      'Data classification made practical — not every piece of data needs the same level of protection.',
    category: 'Data Protection',
    service: 'Data Protection & Backup',
    youtubeId: 'FZXw76-5tHM',
    featured: false,
    order: 9,
  },

  // Business Continuity
  {
    id: 'ransomware-recover',
    title: '🚨 RANSOMWARE TOMORROW. CAN YOU RECOVER?',
    description:
      'Recovery readiness matters as much as prevention — a straight look at what "prepared" actually means.',
    category: 'Business Continuity',
    service: 'Data Protection & Backup',
    youtubeId: '8iBDWxu9194',
    featured: false,
    order: 10,
  },
  {
    id: 'spare-tire',
    title: '🚗 WHAT DOES A SPARE TIRE HAVE TO DO WITH BUSINESS CONTINUITY?',
    description:
      'A simple analogy for why every business needs a plan for when things go wrong.',
    category: 'Business Continuity',
    service: 'Data Protection & Backup',
    youtubeId: 'x_elzkrZDhQ',
    featured: false,
    order: 11,
  },
  {
    id: 'fire',
    title: '🔥 IF YOUR BUSINESS CAUGHT FIRE, WOULD YOU KNOW WHAT TO DO?',
    description:
      'Business continuity and recovery readiness — a straightforward take on why plans matter before something goes wrong.',
    category: 'Business Continuity',
    service: 'Data Protection & Backup',
    youtubeId: '92uWbz4UvyY',
    featured: false,
    order: 12,
  },

  // Vulnerability Management
  {
    id: 'known-vuln',
    title: '⚠️ YOUR NEXT SECURITY BREACH COULD START WITH A VULNERABILITY YOU ALREADY KNOW ABOUT',
    description:
      'Unpatched, known vulnerabilities remain one of the top ways businesses get breached.',
    category: 'Vulnerability Management',
    service: 'Vulnerability Management',
    youtubeId: 'Rzs_rThNhQ4',
    featured: false,
    order: 13,
  },

  // Endpoint & Network Security
  {
    id: 'cloud-simple',
    title: '☁️ STILL CONFUSED ABOUT CLOUD COMPUTING? LET\'S MAKE IT SIMPLE.',
    description:
      'What cloud computing really is, explained in everyday language — and what it means for how you protect your business.',
    category: 'Endpoint & Network Security',
    service: 'Endpoint & Network Security',
    youtubeId: 'oyo9lT3w-8s',
    featured: false,
    order: 14,
  },
  {
    id: 'can-you-tell',
    title: '🚨 CAN YOU TELL WHAT HAPPENED?',
    description:
      'Why logging, monitoring and visibility matter — you can\'t respond to what you can\'t see.',
    category: 'Endpoint & Network Security',
    service: 'Endpoint & Network Security',
    youtubeId: '8v6fAb7uVlA',
    featured: false,
    order: 15,
  },
  {
    id: 'remote-access',
    title: '🔐 IS YOUR BUSINESS REMOTE ACCESS SECURE?',
    description:
      'Remote access is powerful — and easy to get wrong. A practical look at what to check.',
    category: 'Endpoint & Network Security',
    service: 'Endpoint & Network Security',
    youtubeId: 'dR6jX9626gQ',
    featured: false,
    order: 16,
  },

  // Security Awareness
  {
    id: 'people-weakness',
    title: '🚨 WHAT IF THE BIGGEST WEAKNESS IN CYBERSECURITY ISN\'T TECHNOLOGY — BUT PEOPLE?',
    description:
      'Why human risk is central to cybersecurity, and why awareness training is a real control.',
    category: 'Security Awareness',
    service: 'Security Awareness & Training',
    youtubeId: 'aQ1wTbHdxJI',
    featured: true,
    order: 17,
  },
  {
    id: 'delivery-person-1',
    title: '🚨 WOULD YOU TRUST THIS DELIVERY PERSON?',
    description:
      'A simple physical-world example of social engineering — and why the same trick works digitally.',
    category: 'Security Awareness',
    service: 'Security Awareness & Training',
    youtubeId: 'x7qnDET7Ssw',
    featured: false,
    order: 18,
  },
  {
    id: 'delivery-person-2',
    title: '🚨 WOULD YOU TRUST THIS DELIVERY PERSON? (Part 2)',
    description:
      'A follow-up on social engineering — because impersonation is one of the most common attack techniques.',
    category: 'Security Awareness',
    service: 'Security Awareness & Training',
    youtubeId: 'AWH1rR_14fk',
    featured: false,
    order: 19,
  },
  {
    id: 'employee-malware',
    title: '🚨 WHAT HAPPENS WHEN AN EMPLOYEE DOWNLOADS MALWARE?',
    description:
      'A walk-through of what unfolds inside a business when one careless click turns into a real incident.',
    category: 'Security Awareness',
    service: 'Endpoint & Network Security',
    youtubeId: 'LwEeSme0fL8',
    featured: false,
    order: 20,
  },
  {
    id: 'sept-12-update',
    title: 'CYBERSECURITY UPDATE — SEPTEMBER 12, 2026',
    description: 'A short cybersecurity update from The Cyber Guy.',
    category: 'Security Awareness',
    service: 'Security Awareness & Training',
    youtubeId: 'gLPIz1j5X-M',
    featured: false,
    order: 21,
  },

  // Business Cybersecurity
  {
    id: 'two-questions',
    title: '🔐 2 CYBERSECURITY QUESTIONS EVERY BUSINESS SHOULD CHECK TODAY',
    description:
      'Two quick, practical questions every business owner should be able to answer about their security posture.',
    category: 'Business Cybersecurity',
    service: 'Security Assessment',
    youtubeId: '9zS2cLpL_m8',
    featured: true,
    order: 22,
  },
  {
    id: 'wifi-mistake',
    title: '🚨 ALMOST EVERY SMALL BUSINESS MAKES THIS WI-FI SECURITY MISTAKE…',
    description:
      'A common Wi-Fi mistake that quietly weakens small business security — and what to do about it.',
    category: 'Business Cybersecurity',
    service: 'Endpoint & Network Security',
    youtubeId: 'poXHAiuiCdk',
    featured: false,
    order: 23,
  },

  // Cybersecurity Basics
  {
    id: 'network-assets',
    title: '🔐 DO YOU KNOW ALL YOUR NETWORK ASSETS?',
    description:
      'You can\'t protect what you don\'t know you have — a practical prompt on asset inventory.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: 'z8PMffvjk2w',
    featured: false,
    order: 24,
  },
  {
    id: 'ways-in',
    title: '🏠 HOW MANY WAYS CAN SOMEONE GET INTO YOUR BUSINESS?',
    description:
      'Your attack surface — the doors, windows and side gates attackers look at — explained with a simple house analogy.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: 'SjuCaTIq5MY',
    featured: false,
    order: 25,
  },
  {
    id: 'house-cyber',
    title: '🏠 WHAT DOES YOUR HOUSE HAVE TO DO WITH CYBERSECURITY?',
    description:
      'Locks, doors, alarms — the same ideas that protect your home also protect your business.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: 'm-bo2tUg7n8',
    featured: false,
    order: 26,
  },
  {
    id: 'broken-lock',
    title: '🏠 WHAT DOES A HOUSE, A BROKEN LOCK AND A THIEF HAVE TO DO WITH CYBERSECURITY?',
    description:
      'Threats, vulnerabilities and risk — the three words every business owner confuses, made obvious with an everyday example.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: 'bvDN0K3MlE8',
    featured: false,
    order: 27,
  },
  {
    id: 'one-lock',
    title: '🏠 WOULD YOU PROTECT YOUR BUSINESS WITH JUST ONE LOCK?',
    description:
      'Defense in depth — why single controls fail and why layered security is the real answer.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: 'Zkdpk2545Y4',
    featured: false,
    order: 28,
  },
  {
    id: 'control-fails',
    title: '🏠 WHAT HAPPENS WHEN YOUR SECURITY CONTROL FAILS?',
    description:
      'Preventive, detective and corrective controls — and why layered defense actually matters.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: '7q_bME4BPls',
    featured: false,
    order: 29,
  },
  {
    id: 'what-protecting',
    title: '🔐 WHAT ARE WE ACTUALLY PROTECTING IN CYBERSECURITY?',
    description:
      'A back-to-basics look at what security is actually trying to protect — and why that framing matters.',
    category: 'Cybersecurity Basics',
    service: 'Security Assessment',
    youtubeId: '3vIFLtkVLwQ',
    featured: false,
    order: 30,
  },
].map((v) => ({
  ...v,
  youtubeUrl: watchUrl(v.youtubeId),
  embedUrl: embedUrl(v.youtubeId),
  thumbnailUrl: thumbUrl(v.youtubeId),
  slug: v.youtubeId,
}));

export const getVideoBySlug = (slug) => VIDEOS.find((v) => v.slug === slug);
export const getVideosByCategory = (cat) =>
  cat === 'All Videos' ? VIDEOS : VIDEOS.filter((v) => v.category === cat);
export const getVideosByService = (serviceName) =>
  VIDEOS.filter((v) => v.service === serviceName);

// Homepage featured (6, mixed across services)
export const FEATURED_VIDEOS = VIDEOS.filter((v) => v.featured).slice(0, 6);

// Backwards-compat exports for any legacy imports in the codebase
export const searchUrl = (query) =>
  `https://www.youtube.com/results?search_query=${encodeURIComponent(
    'the cyber guy ' + query
  )}`;
export const PLAYLISTS = CATEGORIES.filter((c) => c !== 'All Videos').map((label) => ({
  key: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  label,
}));
export const getVideosForTopics = (topicList = []) => {
  const set = new Set(topicList);
  return VIDEOS.filter((v) => set.has(v.service));
};
